import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, Switch, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { Popper } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { getOffsetLabel } from '@/common/utils/timezoneUtils';
import TimezoneConstant from '@/common/constant/TimezoneConstant';
import TranslationConstant from '@/common/constant/TranslationConstant';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';

import { FormProvider, useForm } from 'react-hook-form';
import Utils from '@/common/utils/utils';
import trI18n from '@/common/utils/trI18n';
import { tr } from '@/common/utils/tr';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useRevalidateOnTranslation from '@/hooks/useRevalidateOnTranslation';
import FormInput from '@/component/common/form/input';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { userAtom } from '@/state/atom/userAtom';
import Constant from '@/common/constant/Constant';
import './UserProfile.css';
import CropImage from '@/component/common/form/crop-image';
import { editUserProfile, saveAutoTimezone } from '@/service/userService';
import { markSelfUpdatingMainLanguage } from '@/component/common/layout/header/handler/HeaderTopHandler';
import 'react-phone-input-2/lib/style.css';
import FormInputWithOnChange from '../common/form/input-with-onchange';
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import { uploadFileToS3 } from '@/service/s3Service';
import { useSnackbar } from 'notistack';
import schema from '@/schema/UserProfileSchema';
import { yupResolver } from '@hookform/resolvers';
import { buildSnackbarMessage } from '@/component/common/SnackbarMessage';
import { getLanguageDisplayName } from '@/common/utils/translationUtils';

const DEFAULT_MAIN_LANGUAGE = TranslationConstant.DEFAULT_MAIN_LANGUAGE;
const MAIN_LANGUAGE_DISPLAY_JP = TranslationConstant.TRANSLATION_LANG_DISPLAY_JP;
const MAIN_LANGUAGE_CODE_LIST = Object.keys(MAIN_LANGUAGE_DISPLAY_JP);

// Custom Popper with elevated z-index so the dropdown renders above the sidebar menu (MUI v4 fix).
const HighZIndexPopper = (props) => (
    <Popper
        {...props}
        style={{ ...props.style, zIndex: 1400 }}
        placement={props.placement}
        className={`${props.className || ''} mainLanguage-autocomplete-popper`}
    />
);

/**
 * Get browser timezone via Intl API.
 * Falls back to Constant.DEFAULT_TIMEZONE when Intl is unavailable.
 */
const getBrowserTimezone = () => {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || Constant.DEFAULT_TIMEZONE;
    } catch (_) {
        return Constant.DEFAULT_TIMEZONE;
    }
};

export default function UserProfileTab(props) {
    // Re-render when the translation dictionary loads/extends so tr() labels update.
    useTranslationSubscription();

    const [userDetail, setUserDetail] = useRecoilState(userAtom);
    const inputFileRef = useRef(null);
    const [inputImage, setInputImage] = useState('');
    const [errorImage, setErrorImage] = useState(false);
    const [openCrop, setOpenCrop] = useState(false);
    const [imagePreview, setImagePreview] = useState(userDetail.avatar);
    const [croppedImage, setCroppedImage] = useState(null);

    // CR-2026-004: main language (主言語). Stored in userSetting.mainLanguage (AWSJSON).
    const currentUserSetting = Utils.convertJSONDataToObject(userDetail?.userSetting) || {};
    const [mainLanguage, setMainLanguage] = useState(
        currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE
    );
    const [isOpenReloadDialog, setIsOpenReloadDialog] = useState(false);
    const [mainLanguageError, setMainLanguageError] = useState('');
    // Track the raw input text so we can detect when user types something invalid.
    const [mainLanguageInputValue, setMainLanguageInputValue] = useState(
        getLanguageDisplayName(currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE)
    );

    // TZL-001: timezone setting. Stored in userSetting.useAutoTimezone + userSetting.timeZone.
    // When useAutoTimezone is not set in DB, treat as true (auto mode = default).
    const dbUseAutoTimezone = !Object.prototype.hasOwnProperty.call(currentUserSetting, 'useAutoTimezone')
        ? true
        : !!currentUserSetting.useAutoTimezone;
    const dbTimezone = currentUserSetting.timeZone || Constant.DEFAULT_TIMEZONE;

    // TZL-001: same fixed list as the field「実行タイムゾーン」(already sorted by `order`)
    const timezoneOptions = useMemo(
        () => TimezoneConstant.TIMEZONE_LIST.map(item => ({ name: item.iana, label: item.label })),
        []
    );

    // Auto mode: list item matching the browser timezone (saved by saveAutoTimezone when the toggle is turned ON).
    // Computed once: getTimezoneForUserIana is costly when the browser timezone is not in the list.
    const browserTimezoneOption = useMemo(() => {
        const browserTz = getBrowserTimezone();
        const item = TimezoneConstant.getTimezoneForUserIana(browserTz);
        return item
            ? { name: item.iana, label: item.label }
            : { name: browserTz, label: `${getOffsetLabel(browserTz, new Date())} ${browserTz}` };
    }, []);

    const [useAutoTimezone, setUseAutoTimezone] = useState(dbUseAutoTimezone);
    // timezone holds the IANA name that will be saved (only relevant when toggle is OFF)
    const [timezone, setTimezone] = useState(dbTimezone);
    const [timezoneInputValue, setTimezoneInputValue] = useState(() => {
        // Initial display: auto mode shows browser tz label; manual mode shows saved tz label
        if (dbUseAutoTimezone) return browserTimezoneOption.label;
        return timezoneOptions.find(t => t.name === dbTimezone)?.label || dbTimezone;
    });
    const [timezoneError, setTimezoneError] = useState('');
    // TZL-001: confirm dialog shown when the toggle is turned ON while other fields still have unsaved changes
    const [isOpenAutoTimezoneConfirm, setIsOpenAutoTimezoneConfirm] = useState(false);

    const closePanelTab = useSetRecoilState(closePanelTabSelector);
    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(schema),
        defaultValues: {
            lastName: userDetail?.lastName
                ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.lastName)
                : '',
            firstName: userDetail?.firstName
                ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.firstName)
                : '',
            email: userDetail?.mail ? userDetail?.mail : '',
        },
    });
	const { handleSubmit, getValues, setValue, register, errors, formState, reset, trigger } = methods;
    // Rebuild error messages that are already shown once the translation dictionary loads.
    useRevalidateOnTranslation(errors, trigger);
	const { setIsLoading } = props;
    const { enqueueSnackbar } = useSnackbar();

    /** Returns true when timezone setting differs from what is saved in DB. */
    const isTimezoneDirty = () => {
        if (useAutoTimezone !== dbUseAutoTimezone) return true;
        if (!useAutoTimezone && timezone !== dbTimezone) return true;
        return false;
    };

    /** Toggle OFF — restore saved DB timezone into the dropdown */
    const showManualTimezone = () => {
        const found = timezoneOptions.find(t => t.name === dbTimezone);
        setTimezone(dbTimezone);
        setTimezoneInputValue(found ? found.label : dbTimezone);
    };

    /** Returns true when fields other than the timezone setting still have unsaved changes. */
    const hasOtherUnsavedChange = () =>
        formState.isDirty ||
        !!croppedImage ||
        mainLanguage !== (currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE);

    /**
     * TZL-001: save the auto timezone (toggle ON) immediately (only userSetting is updated).
     * The loading overlay blocks interaction while saving.
     * On success the reload dialog is shown; other unsaved changes are discarded on reload.
     */
    const saveAutoTimezoneSetting = async () => {
        setIsLoading(true);
        let userSetting = null;
        try {
            userSetting = await saveAutoTimezone(userDetail, TimezoneConstant.getBrowserTimezoneForSave());
        } finally {
            setIsLoading(false);
        }
        if (!userSetting) {
            setUseAutoTimezone(false);
            showManualTimezone();
            enqueueSnackbar(buildSnackbarMessage('AUTH01MSG09'), {
                variant: 'error',
                autoHideDuration: 3000,
            });
            // The timezone setting is back to OFF → only other pending edits keep the unsaved-change flag
            props.setIsProfileHasChange(hasOtherUnsavedChange());
            return;
        }
        setUserDetail(prev => ({ ...prev, userSetting }));
        // OK on the dialog reloads the screen
        setIsOpenReloadDialog(true);
    };

    const handleToggleAutoTimezone = async (e) => {
        const isAuto = e.target.checked;
        setUseAutoTimezone(isAuto);
        setTimezoneError('');

        if (!isAuto) {
            // Toggle OFF is saved with the Save button (a timezone must be selected first)
            showManualTimezone();
            props.setIsProfileHasChange(true);
            return;
        }

        // Toggle ON — show the browser tz (read-only) and always save it.
        // If other fields have unsaved changes, ask for confirmation first (they are discarded on reload).
        setTimezoneInputValue(browserTimezoneOption.label);

        if (hasOtherUnsavedChange()) {
            // The switch stays ON while the confirm dialog is open
            setIsOpenAutoTimezoneConfirm(true);
            return;
        }

        await saveAutoTimezoneSetting();
    };

    /** TZL-001: agree on the confirm dialog — save the auto timezone */
    const handleConfirmAutoTimezone = async () => {
        setIsOpenAutoTimezoneConfirm(false);
        await saveAutoTimezoneSetting();
    };

    /** TZL-001: cancel/close the confirm dialog — set the switch back OFF and keep the other edited data */
    const handleCancelAutoTimezone = () => {
        setIsOpenAutoTimezoneConfirm(false);
        setUseAutoTimezone(false);
        showManualTimezone();
        // Other unsaved changes still exist
        props.setIsProfileHasChange(true);
    };

    const handleSubmitEditUserDetail = async () => {
        const formData = getValues();
        if (errorImage) {
            return;
        }

        // TZL-001: block save when toggle is OFF but no valid timezone is selected.
        if (!useAutoTimezone) {
            const isValidTimezone = timezoneOptions.some(t => t.name === timezone);
            if (!isValidTimezone || !timezone) {
                setTimezoneError(trI18n('COMMSG014'));
                return;
            }
        }

        // Evaluate before saving: after the save, the db values used by isTimezoneDirty may change.
        const isTimezoneChanged = isTimezoneDirty();

        setIsLoading(true);

        let userForUpdate = {
            id: userDetail.id,
            userId: userDetail.mail,
            firstName: formData.firstName,
            lastName: formData.lastName,
            tel: formData?.phoneNumber || userDetail?.tel,
            image: userDetail.image,
            mainLanguage: mainLanguage,
            // TZL-001: auto mode is already saved when the toggle is turned ON → only manual mode is sent here
            useAutoTimezone: useAutoTimezone ? undefined : false,
            timeZone: useAutoTimezone ? undefined : timezone,
        };

        if (croppedImage && croppedImage.file) {
            let filePath = `user_avatar/${userDetail.mail}${new Date().getTime()}`;
            let fileUpload = { ...croppedImage };
            fileUpload.key = filePath;

            let uploadAvatar = await uploadFileToS3(filePath, fileUpload);
            if (!uploadAvatar) {
                setIsLoading(false);
                return;
            }
            userForUpdate.image = filePath;
        }

        const prevMainLanguage = currentUserSetting.mainLanguage || DEFAULT_MAIN_LANGUAGE;
        if (mainLanguage !== prevMainLanguage) {
            userForUpdate.mainLanguage = mainLanguage;
        }
        // Mark before the mutation so the onUserDeleted subscription echo (this same
        // updateUser call, received back on this same tab) does not also open the
        // cross-tab reload dialog on top of isOpenReloadDialog below.
        if (mainLanguage !== prevMainLanguage) {
            markSelfUpdatingMainLanguage();
        }
        const updateResult = await editUserProfile(props.api, userForUpdate);
        if (!updateResult) {
            // Save failed: keep the form state (no reload) so the user does not lose the input
            setIsLoading(false);
            enqueueSnackbar(buildSnackbarMessage('AUTH01MSG09'), {
                variant: 'error',
                autoHideDuration: 3000,
            });
            return;
        }
        enqueueSnackbar(buildSnackbarMessage('COMMSG047'), {
            variant: 'success',
            autoHideDuration: 3000,
        });
        reset(formData);

        // CR-2026-004: when the main language changed (in either direction — JP→non-JP or
        // non-JP→JP), show the confirm dialog (COMMSG030) so the user reloads and the UI
        // switches to the new language (actual translation handled by MLT-001-F1).
        // OK on the dialog triggers window.reload().
        // TZL-001: the same dialog is shown when the timezone setting was changed.
        const isMainLanguageChanged = mainLanguage !== prevMainLanguage;
        if (updateResult && (isMainLanguageChanged || isTimezoneChanged)) {
            if (isMainLanguageChanged) {
                markSelfUpdatingMainLanguage();
            }
            setIsLoading(false);
            setIsOpenReloadDialog(true);
            return;
        }

        window.location.reload();
    };

    useEffect(() => {
        //chưa dùng phoneNumber nên tạm thời ẩn đi
        // register('phoneNumber');
        // setValue('phoneNumber', userDetail?.tel ? userDetail?.tel : '');

        let lastName = userDetail?.lastName
            ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.lastName)
            : '';
        let firstName = userDetail?.firstName
            ? Utils.getTextByLanguage(Constant.LANGUAGE_TYPE.JP, userDetail?.firstName)
            : '';
        setValue('email', userDetail?.mail ? userDetail?.mail : '');
        setValue('lastName', lastName);
        setValue('firstName', firstName);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.submitChangeUserProfileStatus) {
            handleSubmitEditUserDetail().then(() => {
                if (props.submitChangeUserProfileStatus === 1) {
                    props.setTabSelected('persionalSetting');
                } else {
                    closePanelTab({
                        event: null,
                        panelType: Constant.PANEL_TYPE.PERSONAL_MANAGEMENT,
                        panelId: props.panelId,
                        tabId: 'system_user_profile',
                    });
                }
                props.setSubmitChangeUserProfileStatus(0);
            });
        }
    }, [props.submitChangeUserProfileStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    const onFileChange = (event) => {
        let file = event.target.files[0];
        if (Utils.isEmptyObject(file)) {
            return;
        }
        props.setIsProfileHasChange(true);
        Utils.readFileAsArrayBuffer(file).then((reader) => {
            let buffer = new Buffer(reader.result);
            let imgBase64 = buffer.toString('base64');

            setInputImage(`data:${file.type};base64,${imgBase64}`);
            setOpenCrop(true);
            event.target.value = '';
        });
    };

    return (
        <div className="userProfileTab scrollTab">
            <div className="settingFrame">
                <FormProvider {...methods}>
                    <div className="userProfileForm">
                        <div className="userProfileHeader">
                            <div className="userAvatar">
                                <img src={imagePreview} alt="" />
                                <div className="userAvatarInput">
                                    <input
                                        id="contained-button-file-user-profile"
                                        type="file"
                                        accept="image/*"
                                        className="updateFile"
                                        onChange={onFileChange}
                                        ref={inputFileRef}
                                    />
                                    <label>
                                        <Button
                                            onClick={() => inputFileRef.current.click()}
                                            variant="contained"
                                            component="span"
                                        >
                                            {tr('画像ファイルを選択')}
                                        </Button>
                                    </label>
                                    {errorImage && (
                                        <div className="imageError">{trI18n('USER02MSG001')}</div>
                                    )}
                                </div>
                            </div>

                            <div className="form-list-header-control customHeaderbtn">
                                <Button
                                    className="btn-control btn-send  btn-primary btn-hg32"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSubmit(handleSubmitEditUserDetail)}
                                    disabled={
                                        !formState.isValid ||
                                        !!mainLanguageError ||
                                        !!timezoneError ||
                                        (!formState.isDirty &&
                                            !croppedImage &&
                                            mainLanguage ===
                                                (currentUserSetting.mainLanguage ||
                                                    DEFAULT_MAIN_LANGUAGE) &&
                                            !isTimezoneDirty())
                                    }
                                >
                                    {tr('反映')}
                                </Button>
                            </div>
                        </div>

                        <div className="userInfo">
                            <div className="userInfoName">
                                <div className="userInfoItem userInfoLastName">
                                    <div className="userInfoNameLabel">
                                        <span>{tr('姓')}</span>
                                        <span className="star-required">*</span>
                                    </div>
                                    <FormInputWithOnChange
                                        name="lastName"
                                        defaultValue={
                                            userDetail?.lastName
                                                ? Utils.getTextByLanguage(
                                                      Constant.LANGUAGE_TYPE.JP,
                                                      userDetail?.lastName
                                                  )
                                                : ''
                                        }
                                        onChange={() => props.setIsProfileHasChange(true)}
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        variant="outlined"
                                        size="small"
                                        errorlist={errors}
                                    />
                                </div>
                                <div className="userInfoItem customItemMiddle userInfoFirstName">
                                    <div className="userInfoNameLabel">
                                        <span>{tr('名')}</span>
                                        <span className="star-required">*</span>
                                    </div>
                                    <FormInputWithOnChange
                                        name="firstName"
                                        defaultValue={
                                            userDetail?.firstName
                                                ? Utils.getTextByLanguage(
                                                      Constant.LANGUAGE_TYPE.JP,
                                                      userDetail?.firstName
                                                  )
                                                : ''
                                        }
                                        onChange={() => props.setIsProfileHasChange(true)}
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        variant="outlined"
                                        size="small"
                                        errorlist={errors}
                                    />
                                </div>
                            </div>

                            <div className="userInfoItem">
                                <div className="userInfoNameLabel">
                                    <span>{tr('メールアドレス')}</span>
                                </div>
                                <div>
                                    <FormInput
                                        name="email"
                                        className="form-control form-control-wrapInput form-control-hg40"
                                        defaultValue={userDetail.mail}
                                        variant="outlined"
                                        size="small"
                                        disabled
                                    />
                                </div>
                            </div>

                            {/* CR-2026-004: 主言語 (main language) — single-select searchable. */}
                            <div className="userInfoItem mainLanguageSetting">
                                <div className="userInfoNameLabel">
                                    <span>{trI18n('LABEL-MAIN-LANGUAGE')}</span>
                                    <span className="star-required">*</span>
                                </div>
                                <div>
                                    <Autocomplete
                                        options={MAIN_LANGUAGE_CODE_LIST}
                                        getOptionLabel={(option) =>
                                            option ? getLanguageDisplayName(option) : ''
                                        }
                                        getOptionSelected={(option, val) => option === val}
                                        value={
                                            MAIN_LANGUAGE_CODE_LIST.includes(mainLanguage)
                                                ? mainLanguage
                                                : null
                                        }
                                        inputValue={mainLanguageInputValue}
                                        onInputChange={(e, newInputValue, reason) => {
                                            setMainLanguageInputValue(newInputValue);
                                            // When user clears the input box entirely, show error immediately.
                                            if (reason === 'input' && newInputValue === '') {
                                                setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                            } else {
                                                setMainLanguageError('');
                                            }
                                        }}
                                        // Ensure the dropdown popup renders above the sidebar menu (MUI v4 z-index fix).
                                        PopperComponent={HighZIndexPopper}
                                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                                        onChange={(e, selected) => {
                                            if (!selected) {
                                                // User cleared the field — show required error.
                                                setMainLanguage('');
                                                setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                                props.setIsProfileHasChange(true);
                                                return;
                                            }
                                            setMainLanguage(selected);
                                            setMainLanguageError('');
                                            props.setIsProfileHasChange(true);
                                        }}
                                        onBlur={() => {
                                            // When user typed free text that doesn't match any option,
                                            // MUI v4 reverts the input display but mainLanguage stays valid.
                                            // Detect mismatch: if input text doesn't match the current selection label.
                                            const currentLabel = mainLanguage
                                                ? getLanguageDisplayName(mainLanguage)
                                                : '';
                                            if (!mainLanguageInputValue || mainLanguageInputValue !== currentLabel) {
                                                if (!mainLanguage || !MAIN_LANGUAGE_CODE_LIST.includes(mainLanguage)) {
                                                    setMainLanguageError(trI18n('MSG-CM-REQUIRED-INPUT'));
                                                } else {
                                                    // Input was partially typed but reverts to valid — restore display and clear error.
                                                    setMainLanguageInputValue(currentLabel);
                                                    setMainLanguageError('');
                                                }
                                            }
                                        }}
                                        noOptionsText={trI18n('COMMSG001')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                className="form-control form-control-wrapInput form-control-hg40"
                                                error={!!mainLanguageError}
                                            />
                                        )}
                                    />
                                    {mainLanguageError && (
                                        <p
                                            className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error"
                                            style={{
                                                color: '#f44336',
                                                margin: '4px 14px 0',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {mainLanguageError}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* TZL-001: timezone setting */}
                            <div className="userInfoItem timezoneSetting">
                                <div className="userInfoNameLabel">
                                    <span>{tr('タイムゾーン')}</span>
                                    <span className="star-required">*</span>
                                </div>
                                <div className="timezoneSettingContent">
                                    <div className="timezoneToggleRow">
                                        <span className="timezone-toggle-label">
                                            {tr('ブラウザに基づいて、タイムゾーンを自動的に設定')}
                                        </span>
                                        <Switch
                                            checked={useAutoTimezone}
                                            onChange={handleToggleAutoTimezone}
                                        />
                                    </div>
                                    <Autocomplete
                                        options={timezoneOptions}
                                        getOptionLabel={(option) =>
                                            typeof option === 'string' ? option : option.label
                                        }
                                        getOptionSelected={(option, val) =>
                                            option.name === (val?.name || val)
                                        }
                                        value={
                                            useAutoTimezone
                                                ? browserTimezoneOption
                                                : timezoneOptions.find(t => t.name === timezone) || null
                                        }
                                        inputValue={timezoneInputValue}
                                        disabled={useAutoTimezone}
                                        PopperComponent={HighZIndexPopper}
                                        popupIcon={<ExpandMoreIcon fontSize="small" />}
                                        onInputChange={(_e, newInputValue, reason) => {
                                            if (useAutoTimezone) return;
                                            setTimezoneInputValue(newInputValue);
                                            if (reason === 'input' && newInputValue === '') {
                                                setTimezoneError(trI18n('COMMSG014'));
                                            } else if (reason === 'input') {
                                                setTimezoneError('');
                                            }
                                        }}
                                        onChange={(_e, selected) => {
                                            if (!selected) {
                                                setTimezone('');
                                                setTimezoneError(trI18n('COMMSG014'));
                                                props.setIsProfileHasChange(true);
                                                return;
                                            }
                                            setTimezone(selected.name);
                                            setTimezoneError('');
                                            props.setIsProfileHasChange(true);
                                        }}
                                        onBlur={() => {
                                            if (useAutoTimezone) return;
                                            // If user typed free text that doesn't match any option,
                                            // revert display to the last valid selection or show error.
                                            const currentLabel =
                                                timezoneOptions.find(t => t.name === timezone)?.label || '';
                                            if (timezoneInputValue !== currentLabel) {
                                                if (
                                                    !timezone ||
                                                    !timezoneOptions.some(t => t.name === timezone)
                                                ) {
                                                    setTimezoneError(trI18n('COMMSG014'));
                                                } else {
                                                    setTimezoneInputValue(currentLabel);
                                                    setTimezoneError('');
                                                }
                                            }
                                        }}
                                        noOptionsText={trI18n('COMMSG001')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                size="small"
                                                className="form-control form-control-wrapInput form-control-hg40"
                                                error={!!timezoneError}
                                            />
                                        )}
                                    />
                                    {timezoneError && (
                                        <p
                                            className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error"
                                            style={{
                                                color: '#f44336',
                                                margin: '4px 14px 0',
                                                fontSize: '0.75rem',
                                            }}
                                        >
                                            {timezoneError}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <DataChangePleaseReloadModal
                            isOpen={isOpenReloadDialog}
                            setIsOpen={setIsOpenReloadDialog}
                        />
                        {/* TZL-001: confirm saving the auto timezone while other fields have unsaved changes */}
                        <Dialog
                            open={isOpenAutoTimezoneConfirm}
                            onClose={handleCancelAutoTimezone}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            className="wrapDialog"
                        >
                            <DialogContent>
                                <div className="confirm-dialog-content">{trI18n('COMMSG004')}</div>
                            </DialogContent>
                            <DialogActions className="confirm-dialog-action">
                                {/* btn confirm */}
                                <Button
                                    className="btn-hg32 btn-handle-delete-user-agree"
                                    variant="contained"
                                    onClick={handleConfirmAutoTimezone}
                                >
                                    {trI18n('COMBTN002')}
                                </Button>
                                {/* btn cancel */}
                                <Button
                                    className="btn-light btn-hg32"
                                    variant="contained"
                                    onClick={handleCancelAutoTimezone}
                                >
                                    {trI18n('COMBTN007')}
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </div>
                </FormProvider>
            </div>
            {/* crop image */}
            <CropImage
                openCrop={openCrop}
                setOpenCrop={setOpenCrop}
                aspect={1}
                cropShape={'round'}
                inputImage={inputImage}
                setInputImage={setInputImage}
                setImagePreview={setImagePreview}
                setCroppedImage={setCroppedImage}
                setErrorImage={setErrorImage}
            />
        </div>
    );
}
